import Phaser from 'phaser';
import { COLORS, FONT_HEADER } from '../style.js';
import { DESIGN_WIDTH, LAYOUT } from '../PhaserGame.js';

// Toast geometry — pill that slots just inside the top of the map zone.
// `TOAST_Y` is read from LAYOUT inside `show()` rather than at module
// top level: this view is imported transitively from `PhaserGame.ts`
// (via GameScene) and reading LAYOUT during module init creates a TDZ
// circular-dep error.
const TOAST_WIDTH = 520;
const TOAST_HEIGHT = 60;
const TOAST_RADIUS = 8;
const TOAST_DEPTH = 500;

// Animation timings — quick fade-in, brief hold, slower fade-out so the
// player has time to read it without it lingering through the AI's next
// move.
const FADE_IN_MS = 200;
const HOLD_MS = 1700;
const FADE_OUT_MS = 400;

/**
 * One-shot top-of-map banner that announces a player elimination. The
 * banner is built fresh per call (no persistent instance state) and
 * destroys itself after fading out; calling `show` again while one is
 * already on screen replaces it cleanly.
 *
 * Color: dark navy fill with a 2px team-color stroke and team-color
 * label. Stroke + label are the only team-tinted elements so the toast
 * reads consistently regardless of how dark or light the team color is.
 */
export class EliminationToast {
  private readonly scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | undefined;
  private tween: Phaser.Tweens.Tween | undefined;
  private onCompleteCb: (() => void) | undefined;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * Show the elimination banner for `teamName` in `teamColor`. If
   * `isLocalPlayer` is true, copy switches to second person ("YOU
   * HAVE BEEN ELIMINATED"); otherwise the third-person team-name
   * banner runs. The gate must be "is the LOCAL player the one who
   * got eliminated" — historically this was `isHuman`, which was
   * fine in single-player vs AI (only one human seat = local) but
   * shows the wrong copy in multiplayer where multiple humans are
   * connected and only one of them is "you." `onComplete` fires
   * after the fade-out finishes — used by the scene to gate the
   * game-over modal so the toast renders first.
   */
  show(
    teamName: string,
    teamColor: string,
    isLocalPlayer: boolean,
    onComplete?: () => void
  ): void {
    this.dismissImmediate();
    this.onCompleteCb = onComplete;

    const colorInt = Phaser.Display.Color.HexStringToColor(teamColor).color;
    const cx = DESIGN_WIDTH / 2;
    const toastY = LAYOUT.topSafe + LAYOUT.hudHeight + 36;

    const bg = this.scene.add.graphics();
    bg.fillStyle(COLORS.bgPanelInt, 0.96);
    bg.fillRoundedRect(
      -TOAST_WIDTH / 2,
      -TOAST_HEIGHT / 2,
      TOAST_WIDTH,
      TOAST_HEIGHT,
      TOAST_RADIUS
    );
    bg.lineStyle(2, colorInt, 1);
    bg.strokeRoundedRect(
      -TOAST_WIDTH / 2,
      -TOAST_HEIGHT / 2,
      TOAST_WIDTH,
      TOAST_HEIGHT,
      TOAST_RADIUS
    );

    const message = isLocalPlayer
      ? 'YOU HAVE BEEN ELIMINATED'
      : `${teamName.toUpperCase()} ELIMINATED`;

    const label = this.scene.add
      .text(0, 0, message, {
        fontFamily: FONT_HEADER,
        fontSize: '20px',
        color: teamColor,
        fontStyle: '700',
      })
      .setLetterSpacing(3)
      .setOrigin(0.5);

    const container = this.scene.add.container(cx, toastY, [bg, label]);
    container.setDepth(TOAST_DEPTH);
    container.setAlpha(0);
    this.container = container;

    // Three-step tween: fade in → hold → fade out → cleanup.
    this.tween = this.scene.tweens.add({
      targets: container,
      alpha: 1,
      duration: FADE_IN_MS,
      onComplete: () => {
        this.scene.time.delayedCall(HOLD_MS, () => {
          if (!this.container) return;
          this.tween = this.scene.tweens.add({
            targets: this.container,
            alpha: 0,
            duration: FADE_OUT_MS,
            onComplete: () => {
              this.cleanup();
              const cb = this.onCompleteCb;
              this.onCompleteCb = undefined;
              cb?.();
            },
          });
        });
      },
    });
  }

  /**
   * Tear down without firing the onComplete callback — used when a new
   * toast supersedes the current one before it finished.
   */
  private dismissImmediate(): void {
    this.tween?.stop();
    this.tween = undefined;
    this.cleanup();
    this.onCompleteCb = undefined;
  }

  private cleanup(): void {
    this.container?.destroy();
    this.container = undefined;
  }
}
